<template>
  <div class="page-container bg-surface-50 dark:bg-surface-950">
    <Navbar />

    <LoadingSpinner v-if="isLoading" is-full-page message="Loading analytics data..." />

    <main v-else class="page-content">
      <!-- Header -->
      <div class="page-header animate-fade-up">
        <Breadcrumbs :items="[{ label: 'Analytics' }]" />
        <div class="flex items-start justify-between mt-4">
          <div>
            <h1 class="text-display-sm text-surface-900 dark:text-surface-50 mb-1">
              Fleet Analytics
            </h1>
            <p class="text-surface-500 dark:text-surface-400 text-sm">
              Fleet operations intelligence &amp; sustainability performance
            </p>
          </div>
          <div class="flex gap-3">
            <BaseButton variant="secondary" @click="exportAllData" :icon="ArrowDownTrayIcon">
              Export All Data
            </BaseButton>
            <BaseButton variant="primary" @click="refreshData" :icon="ArrowPathIcon" :loading="isRefreshing">
              Refresh
            </BaseButton>
          </div>
        </div>
      </div>

      <PortalAdminCustomerPicker
        v-if="isPortalSuperAdmin"
        class="mb-6 animate-fade-up"
        style="animation-delay: 20ms"
      />

      <!-- Empty-state: account not yet configured in MCS -->
      <template v-if="customersMcs.length === 0">
        <div class="mb-6 animate-fade-up" style="animation-delay: 40ms">
          <EmptyState
            :icon="AdjustmentsHorizontalIcon"
            :title="isPortalSuperAdmin ? 'No customer selected' : 'Account setup in progress'"
            :message="isPortalSuperAdmin
              ? 'Use the picker above to choose which customer\'s portal to view.'
              : 'Your account is being set up. Please contact your administrator.'"
            variant="info"
          />
        </div>
      </template>

      <!-- Customer Selector -->
      <div
        v-else-if="customersMcs.length > 1"
        class="mb-6 animate-fade-up"
        style="animation-delay: 40ms"
      >
        <div class="border-b border-surface-200 dark:border-surface-700">
          <nav class="-mb-px flex space-x-1 overflow-x-auto" aria-label="Tabs">
            <button
              v-for="(customer, index) in customersMcs"
              :key="customer.mcs_customer_id"
              @click="selectedCustomerIndex = index"
              :class="[
                selectedCustomerIndex === index
                  ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                  : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300 dark:text-surface-400 dark:hover:text-surface-300',
                'whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors duration-200'
              ]"
            >
              {{ customer.customer_name }}
            </button>
          </nav>
        </div>
      </div>

      <!-- Filters -->
      <div class="animate-fade-up mb-8" style="animation-delay: 60ms">
        <BaseCard class="sticky top-4 z-10 border border-surface-200/60 dark:border-surface-700/60 backdrop-blur-sm bg-white/80 dark:bg-surface-900/80 rounded-2xl">
          <template #header>
            <div class="flex items-center gap-3">
              <div class="p-1.5 rounded-lg bg-accent-500/10">
                <AdjustmentsHorizontalIcon class="h-5 w-5 text-accent-500" />
              </div>
              <h3 class="text-title text-surface-900 dark:text-surface-50">Filters &amp; Date Range</h3>
            </div>
          </template>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label class="label">Date Range:</label>
              <VueMultiselect
                v-model="selectedDateRange"
                :options="dateRanges"
                label="label"
                track-by="value"
                placeholder="Select Date Range"
                class="multiselect-tailwind"
              />
            </div>
            <div v-if="selectedDateRange.value === 'custom'" class="md:col-span-2">
              <DateRangePicker v-model="customRange" :min="minDate" :max="maxDate" />
            </div>
            <div :class="selectedDateRange.value === 'custom' ? 'md:col-span-1' : 'md:col-span-2'">
              <EntityFilter
                v-model="selectedEntities"
                :options="allEntities"
                :label="`${entityLabel} Filter`"
                :placeholder="`Select ${entityLabel}s`"
              />
            </div>
          </div>
        </BaseCard>
      </div>

      <!-- Fleet Command Center - 6 KPI Cards -->
      <div class="animate-fade-up mb-8" style="animation-delay: 100ms">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-px flex-1 bg-gradient-to-r from-accent-500/40 to-transparent"></div>
          <span class="text-xs font-bold uppercase tracking-[0.2em] text-accent-600 dark:text-accent-400">Fleet Command</span>
          <div class="h-px flex-1 bg-gradient-to-l from-accent-500/40 to-transparent"></div>
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div
            v-for="(metric, index) in fleetMetrics"
            :key="index"
            class="kpi-card group"
            :style="{ animationDelay: `${100 + index * 50}ms` }"
          >
            <div class="absolute left-0 top-0 bottom-0 w-1 rounded-full" :style="{ backgroundColor: metric.color }"></div>
            <div class="pl-3">
              <p class="text-[10px] font-bold text-surface-400 dark:text-surface-500 uppercase tracking-[0.12em] mb-1.5 leading-tight">
                {{ metric.label }}
              </p>
              <p class="text-xl font-extrabold font-mono text-surface-900 dark:text-surface-50 tabular-nums leading-none">
                {{ metric.value }}
              </p>
              <div v-if="metric.change !== null && metric.change !== undefined" class="flex items-center mt-2 gap-1">
                <span
                  :class="[
                    'inline-flex items-center text-xs font-semibold font-mono px-1.5 py-0.5 rounded-md',
                    metric.change >= 0
                      ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10'
                      : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10'
                  ]"
                >
                  <component :is="metric.change >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon" class="h-3 w-3 mr-0.5" />
                  {{ formatChange(metric.change) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sustainability Impact Hub -->
      <div class="animate-fade-up mb-8" style="animation-delay: 160ms">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-px flex-1 bg-gradient-to-r from-emerald-500/40 to-transparent"></div>
          <span class="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">Sustainability Impact</span>
          <div class="h-px flex-1 bg-gradient-to-l from-emerald-500/40 to-transparent"></div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <!-- CO2 Prevented -->
            <div class="impact-metric-card border-emerald-200/60 dark:border-emerald-800/30">
              <div class="impact-icon bg-emerald-100 dark:bg-emerald-900/30">
                <CloudIcon class="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div class="text-2xl font-extrabold font-mono text-emerald-700 dark:text-emerald-300 mt-3 tabular-nums">
                {{ formatCompact(totalCo2Savings) }}
              </div>
              <div class="text-xs font-medium text-emerald-600/80 dark:text-emerald-400/80 mt-0.5">Kg CO2 Prevented</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                {{ formatCompact(co2PerAsset) }} kg per {{ entityLabel.toLowerCase() }}
              </div>
            </div>

            <!-- Equivalent Trees -->
            <div class="impact-metric-card border-teal-200/60 dark:border-teal-800/30">
              <div class="impact-icon bg-teal-100 dark:bg-teal-900/30">
                <svg class="h-5 w-5 text-teal-600 dark:text-teal-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22V8M5 12l7-8 7 8M7 17l5-5 5 5"/></svg>
              </div>
              <div class="text-2xl font-extrabold font-mono text-teal-700 dark:text-teal-300 mt-3 tabular-nums">
                {{ formatNumber(equivalentTrees) }}
              </div>
              <div class="text-xs font-medium text-teal-600/80 dark:text-teal-400/80 mt-0.5">Equivalent Trees Planted</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                {{ (equivalentTrees / 365).toFixed(1) }} trees per day
              </div>
            </div>

            <!-- Cars Off Road -->
            <div class="impact-metric-card border-blue-200/60 dark:border-blue-800/30">
              <div class="impact-icon bg-blue-100 dark:bg-blue-900/30">
                <TruckIcon class="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div class="text-2xl font-extrabold font-mono text-blue-700 dark:text-blue-300 mt-3 tabular-nums">
                {{ formatNumber(equivalentCars) }}
              </div>
              <div class="text-xs font-medium text-blue-600/80 dark:text-blue-400/80 mt-0.5">Cars Off Road Equivalent</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                annual emissions offset
              </div>
            </div>

            <!-- Carbon Intensity -->
            <div class="impact-metric-card border-violet-200/60 dark:border-violet-800/30">
              <div class="impact-icon bg-violet-100 dark:bg-violet-900/30">
                <FireIcon class="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div class="text-2xl font-extrabold font-mono text-violet-700 dark:text-violet-300 mt-3 tabular-nums">
                {{ carbonIntensity }}
              </div>
              <div class="text-xs font-medium text-violet-600/80 dark:text-violet-400/80 mt-0.5">Kg CO2 Saved / Litre</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                biofuel displacement factor
              </div>
            </div>

            <!-- Avg CO2 per Transaction -->
            <div class="impact-metric-card border-lime-200/60 dark:border-lime-800/30">
              <div class="impact-icon bg-lime-100 dark:bg-lime-900/30">
                <CheckBadgeIcon class="h-5 w-5 text-lime-600 dark:text-lime-400" />
              </div>
              <div class="text-2xl font-extrabold font-mono text-lime-700 dark:text-lime-300 mt-3 tabular-nums">
                {{ formatCompact(avgCo2PerTransaction) }}
              </div>
              <div class="text-xs font-medium text-lime-600/80 dark:text-lime-400/80 mt-0.5">Kg CO2 Saved / Trip</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                avg per fueling transaction
              </div>
            </div>

            <!-- Cumulative Savings -->
            <div class="impact-metric-card border-cyan-200/60 dark:border-cyan-800/30">
              <div class="impact-icon bg-cyan-100 dark:bg-cyan-900/30">
                <ArrowTrendingUpIcon class="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div class="text-2xl font-extrabold font-mono text-cyan-700 dark:text-cyan-300 mt-3 tabular-nums">
                {{ formatCompact(cumulativeCo2Trend) }}
              </div>
              <div class="text-xs font-medium text-cyan-600/80 dark:text-cyan-400/80 mt-0.5">Cumulative CO2 Saved</div>
              <div class="text-[10px] text-surface-400 dark:text-surface-600 mt-2 font-mono">
                running total this period
              </div>
            </div>
        </div>
      </div>

      <!-- Fleet Operations Intelligence -->
      <div class="animate-fade-up mb-8" style="animation-delay: 200ms">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-px flex-1 bg-gradient-to-r from-primary-500/40 to-transparent"></div>
          <span class="text-xs font-bold uppercase tracking-[0.2em] text-primary-500 dark:text-primary-300">Fleet Operations</span>
          <div class="h-px flex-1 bg-gradient-to-l from-primary-500/40 to-transparent"></div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <!-- Day of Week Activity -->
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-primary-500/10">
                  <CalendarDaysIcon class="h-5 w-5 text-primary-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Weekly Activity Pattern</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Fueling volume by day of week</p>
                </div>
              </div>
            </template>
            <div class="space-y-2.5">
              <div v-for="day in dayOfWeekData" :key="day.name" class="flex items-center gap-3">
                <span class="text-[11px] font-mono font-semibold text-surface-500 dark:text-surface-400 w-8 shrink-0">{{ day.abbr }}</span>
                <div class="flex-1 h-6 bg-surface-100 dark:bg-surface-800/60 rounded-md overflow-hidden relative">
                  <div
                    class="h-full rounded-md transition-all duration-700 ease-out"
                    :style="{
                      width: day.percentage + '%',
                      background: `linear-gradient(90deg, ${day.color}cc, ${day.color})`
                    }"
                  ></div>
                  <span class="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-mono font-bold text-surface-600 dark:text-surface-300">
                    {{ formatCompact(day.volume) }} L
                  </span>
                </div>
                <span class="text-[10px] font-mono text-surface-400 dark:text-surface-500 w-10 text-right tabular-nums">{{ day.count }}x</span>
              </div>
            </div>
          </BaseCard>

          <!-- Asset Utilization Distribution -->
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-accent-500/10">
                  <ChartPieIcon class="h-5 w-5 text-accent-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">{{ entityLabel }} Utilization</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Volume distribution across fleet</p>
                </div>
              </div>
            </template>
            <div class="h-64">
              <PieChart
                :chart-data="assetDistributionData"
                :chart-options="doughnutChartOptions"
                :key="chartKey"
              />
            </div>
          </BaseCard>

          <!-- Fueling Frequency Breakdown -->
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-amber-500/10">
                  <BoltIcon class="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Fueling Frequency</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Transactions per {{ entityLabel.toLowerCase() }}</p>
                </div>
              </div>
            </template>
            <div class="space-y-2">
              <div v-for="(asset, index) in topAssetsByFrequency.slice(0, 8)" :key="asset.name" class="flex items-center gap-2">
                <span class="text-[10px] font-mono font-bold text-surface-300 dark:text-surface-600 w-4 tabular-nums">{{ index + 1 }}</span>
                <span class="text-xs font-medium text-surface-700 dark:text-surface-300 truncate flex-1 min-w-0">{{ asset.name }}</span>
                <div class="flex items-center gap-1.5">
                  <div class="flex gap-px">
                    <div
                      v-for="i in Math.min(asset.count, 12)"
                      :key="i"
                      class="w-1.5 h-4 rounded-sm"
                      :style="{ backgroundColor: i <= 4 ? '#10B981' : i <= 8 ? '#F59E0B' : '#EF4444', opacity: 0.6 + (i / asset.count) * 0.4 }"
                    ></div>
                  </div>
                  <span class="text-[10px] font-mono font-bold text-surface-500 dark:text-surface-400 w-6 text-right tabular-nums">{{ asset.count }}</span>
                </div>
              </div>
              <div v-if="topAssetsByFrequency.length === 0" class="text-center py-8 text-xs text-surface-400">
                No transaction data available
              </div>
            </div>
          </BaseCard>
        </div>
      </div>

      <!-- Performance Trend Charts -->
      <div class="space-y-8">
        <div class="animate-fade-up" style="animation-delay: 240ms">
          <div class="flex items-center gap-2 mb-4">
            <div class="h-px flex-1 bg-gradient-to-r from-accent-500/40 to-transparent"></div>
            <span class="text-xs font-bold uppercase tracking-[0.2em] text-accent-600 dark:text-accent-400">Performance Trends</span>
            <div class="h-px flex-1 bg-gradient-to-l from-accent-500/40 to-transparent"></div>
          </div>
        </div>

        <!-- Row 1: Consumption + CO2 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style="animation-delay: 260ms">
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-accent-500/10">
                    <ChartBarIcon class="h-5 w-5 text-accent-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Fuel Consumption Trends</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('volume-trend')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <LineChart :chart-data="volumeOverTimeData" :chart-options="lineChartOptions" :key="chartKey" />
            </div>
          </BaseCard>

          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-emerald-500/10">
                    <CloudIcon class="h-5 w-5 text-emerald-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">CO2 Savings Over Time</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('co2-trend')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <LineChart :chart-data="co2SavingsOverTimeData" :chart-options="lineChartOptions" :key="chartKey" />
            </div>
          </BaseCard>
        </div>

        <!-- Row 2: Top Assets + Monthly Performance -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style="animation-delay: 300ms">
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-accent-500/10">
                    <TruckIcon class="h-5 w-5 text-accent-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Top {{ entityLabel }}s by Volume</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('top-entities')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <BarChart :chart-data="topEntitiesData" :chart-options="horizontalBarOptions" :key="chartKey" />
            </div>
          </BaseCard>

          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-accent-500/10">
                    <CalendarIcon class="h-5 w-5 text-accent-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Monthly Performance</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('monthly-performance')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <BarChart :chart-data="monthlyPerformanceData" :chart-options="monthlyBarOptions" :key="chartKey" />
            </div>
          </BaseCard>
        </div>

        <!-- Row 3: Fuel Distribution + Cumulative CO2 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style="animation-delay: 340ms">
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-accent-500/10">
                    <ChartPieIcon class="h-5 w-5 text-accent-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Fuel Type Distribution</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('fuel-distribution')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <PieChart :chart-data="fuelTypeDistributionData" :chart-options="pieChartOptions" :key="chartKey" />
            </div>
          </BaseCard>

          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="p-1.5 rounded-lg bg-emerald-500/10">
                    <ArrowTrendingUpIcon class="h-5 w-5 text-emerald-500" />
                  </div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Cumulative CO2 Impact</h3>
                </div>
                <BaseButton variant="secondary" size="sm" @click="downloadChart('cumulative-co2')" :icon="ArrowDownTrayIcon">
                  PNG
                </BaseButton>
              </div>
            </template>
            <div class="h-80">
              <LineChart :chart-data="cumulativeCo2Data" :chart-options="cumulativeLineOptions" :key="chartKey" />
            </div>
          </BaseCard>
        </div>
      </div>

      <!-- Asset Performance Rankings -->
      <div class="animate-fade-up mt-8 mb-8" style="animation-delay: 380ms">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-px flex-1 bg-gradient-to-r from-primary-500/40 to-transparent"></div>
          <span class="text-xs font-bold uppercase tracking-[0.2em] text-primary-500 dark:text-primary-300">{{ entityLabel }} Rankings</span>
          <div class="h-px flex-1 bg-gradient-to-l from-primary-500/40 to-transparent"></div>
        </div>

        <BaseCard class="analytics-card">
          <template #header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-primary-500/10">
                  <TrophyIcon class="h-5 w-5 text-primary-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">{{ entityLabel }} Performance Overview</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Ranked by total fuel consumption &mdash; showing volume, transactions, CO2 impact</p>
                </div>
              </div>
            </div>
          </template>

          <div class="overflow-x-auto">
            <table class="w-full text-left" v-if="assetRankings.length">
              <thead>
                <tr class="border-b border-surface-200/60 dark:border-surface-700/60">
                  <th class="ranking-th w-10">#</th>
                  <th class="ranking-th">{{ entityLabel }}</th>
                  <th class="ranking-th text-right">Volume (L)</th>
                  <th class="ranking-th text-right">Transactions</th>
                  <th class="ranking-th text-right">Avg / Txn</th>
                  <th class="ranking-th text-right">CO2 Saved (Kg)</th>
                  <th class="ranking-th w-48">Volume Share</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(asset, index) in assetRankings.slice(0, 15)"
                  :key="asset.name"
                  class="ranking-row"
                >
                  <td class="ranking-td">
                    <span
                      :class="[
                        'inline-flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-bold',
                        index < 3 ? 'bg-accent-100 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300' : 'bg-surface-100 text-surface-500 dark:bg-surface-800 dark:text-surface-400'
                      ]"
                    >{{ index + 1 }}</span>
                  </td>
                  <td class="ranking-td font-semibold text-surface-900 dark:text-surface-100">
                    {{ asset.name }}
                  </td>
                  <td class="ranking-td text-right font-mono tabular-nums font-semibold">
                    {{ formatNumber(Math.round(asset.volume)) }}
                  </td>
                  <td class="ranking-td text-right font-mono tabular-nums">
                    {{ asset.transactions }}
                  </td>
                  <td class="ranking-td text-right font-mono tabular-nums text-surface-500 dark:text-surface-400">
                    {{ formatNumber(Math.round(asset.avgPerTxn)) }}
                  </td>
                  <td class="ranking-td text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">
                    {{ formatNumber(Math.round(asset.co2Saved)) }}
                  </td>
                  <td class="ranking-td">
                    <div class="flex items-center gap-2">
                      <div class="flex-1 h-2 bg-surface-100 dark:bg-surface-800 rounded-full overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all duration-500"
                          :style="{
                            width: asset.sharePercent + '%',
                            background: `linear-gradient(90deg, #10B981, #059669)`
                          }"
                        ></div>
                      </div>
                      <span class="text-[10px] font-mono font-semibold text-surface-400 dark:text-surface-500 w-10 text-right tabular-nums">
                        {{ asset.sharePercent.toFixed(1) }}%
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center py-12 text-sm text-surface-400">
              No ranking data available for the selected period
            </div>
          </div>
        </BaseCard>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { authFetch } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { formatInTimeZone } from 'date-fns-tz'
import { savingsFactorFor } from '@/utils/emissions'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import EntityFilter from '@/components/dashboard/EntityFilter.vue'
import DateRangePicker from '@/components/dashboard/DateRangePicker.vue'
import PortalAdminCustomerPicker from '@/components/PortalAdminCustomerPicker.vue'
import { useEffectiveCustomers } from '@/composables/useEffectiveCustomers'
import { useCustomerDataRange } from '@/composables/useCustomerDataRange'
import LineChart from '@/components/LineChart.vue'
import BarChart from '@/components/BarChart.vue'
import PieChart from '@/components/PieChart.vue'
import VueMultiselect from 'vue-multiselect'

import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CloudIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  TruckIcon,
  BeakerIcon,
  ChartPieIcon,
  CalendarIcon,
  CalendarDaysIcon,
  BoltIcon,
  FireIcon,
  CheckBadgeIcon,
  TrophyIcon,
  DocumentTextIcon,
  CubeIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const { success, error: showError } = useToast()

const userProfile = computed(() => store.state.userProfile)
const { isPortalSuperAdmin, customersMcs } = useEffectiveCustomers()
const isLoading = ref(true)
const isRefreshing = ref(false)

const selectedCustomerIndex = ref(0)
const selectedDateRange = ref({ label: 'Last 6 Months', value: 'last6months' })
const dateRanges = [
  { label: 'Last Week', value: 'lastweek' },
  { label: 'Last Month', value: 'lastmonth' },
  { label: 'Last 3 Months', value: 'last3months' },
  { label: 'Last 6 Months', value: 'last6months' },
  { label: 'Last Year', value: 'lastyear' },
  { label: 'Year-to-Date (YTD)', value: 'ytd' },
  { label: 'Custom', value: 'custom' },
]

const startDate = ref('')
const endDate = ref('')
const comparisonStartDate = ref('')
const comparisonEndDate = ref('')

const customRange = computed({
  get: () => ({ startDate: startDate.value, endDate: endDate.value }),
  set: (v) => {
    startDate.value = v.startDate
    endDate.value = v.endDate
  }
})
const { minDate, maxDate } = useCustomerDataRange(
  computed(() => {
    const entry = customersMcs.value[selectedCustomerIndex.value]
    return entry ? [entry.mcs_customer_id] : []
  })
)

const selectedEntities = ref([])
const allEntities = ref([])
const transactions = ref([])
const previousTransactions = ref([])

const chartKey = ref(0)

// Chart data refs
const volumeOverTimeData = ref({ labels: [], datasets: [] })
const topEntitiesData = ref({ labels: [], datasets: [] })
const co2SavingsOverTimeData = ref({ labels: [], datasets: [] })
const fuelTypeDistributionData = ref({ labels: [], datasets: [] })
const monthlyPerformanceData = ref({ labels: [], datasets: [] })
const cumulativeCo2Data = ref({ labels: [], datasets: [] })
const assetDistributionData = ref({ labels: [], datasets: [] })

// Themed chart colors
const chartColors = {
  primary: '#003A6D',
  accent: '#10B981',
  accentDark: '#059669',
  teal: '#0D9488',
  emerald: '#047857',
  blue: '#0891B2',
  amber: '#F59E0B',
  violet: '#7C3AED',
  rose: '#F43F5E',
  slate: '#64748B',
  palette: ['#10B981', '#0891B2', '#003A6D', '#7C3AED', '#F59E0B', '#F43F5E', '#0D9488', '#8B5CF6', '#EC4899', '#06B6D4']
}

// Chart options
const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8 } },
  },
  scales: {
    x: {
      type: 'time',
      time: { unit: 'day', displayFormats: { day: 'dd MMM' } },
      ticks: { autoSkip: true, maxTicksLimit: 10 },
      grid: { display: false },
    },
    y: { beginAtZero: true, grid: { drawBorder: false } },
  },
  elements: {
    line: { tension: 0.35, borderWidth: 2.5 },
    point: { radius: 2, hoverRadius: 5 },
  },
}

const cumulativeLineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8 } },
  },
  scales: {
    x: {
      type: 'time',
      time: { unit: 'day', displayFormats: { day: 'dd MMM' } },
      ticks: { autoSkip: true, maxTicksLimit: 10 },
      grid: { display: false },
    },
    y: { beginAtZero: true, grid: { drawBorder: false } },
  },
  elements: {
    line: { tension: 0.3, borderWidth: 2.5 },
    point: { radius: 0, hoverRadius: 4 },
  },
}

const horizontalBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: {
    x: { beginAtZero: true, grid: { drawBorder: false } },
    y: { grid: { display: false } },
  },
}

const monthlyBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8 } },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, grid: { drawBorder: false } },
  },
}

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8 } },
  },
}

const doughnutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '55%',
  plugins: {
    legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyleWidth: 8, font: { size: 11 } } },
  },
}

const fleetMetrics = ref([])

const entityLabel = computed(() =>
  userProfile.value && userProfile.value.group ? 'Vessel' : 'Asset'
)

const comparisonLabel = ref('')

// Per-customer CO2-savings factor (kg saved / litre). This page is scoped to a
// single customer (selectedCustomerIndex), so default to that customer.
const co2FactorFor = (mcsCustomerId) =>
  savingsFactorFor(
    mcsCustomerId ?? customersMcs.value[selectedCustomerIndex.value]?.mcs_customer_id,
    customersMcs.value
  )

// Sustainability computeds
const totalCo2Savings = computed(() =>
  transactions.value.reduce((sum, txn) => sum + (txn.quantity ?? 0) * co2FactorFor(), 0)
)

const equivalentTrees = computed(() => Math.round(totalCo2Savings.value * 0.05))
const equivalentCars = computed(() => Math.round(totalCo2Savings.value * 0.0004))

const co2PerAsset = computed(() => {
  const uniqueAssets = new Set(transactions.value.map(t => t.vehicle_no ?? '')).size
  return uniqueAssets > 0 ? totalCo2Savings.value / uniqueAssets : 0
})

const carbonIntensity = computed(() => co2FactorFor().toFixed(2))

const avgCo2PerTransaction = computed(() => {
  if (transactions.value.length === 0) return 0
  return totalCo2Savings.value / transactions.value.length
})

const cumulativeCo2Trend = computed(() => totalCo2Savings.value)

const sustainabilityScore = computed(() => {
  const co2 = totalCo2Savings.value
  const assetCount = new Set(transactions.value.map(t => t.vehicle_no ?? '')).size
  // Score based on total CO2 impact and fleet participation
  let score = 50 // baseline — all fuel is biodiesel
  if (co2 > 1000) score += 10
  if (co2 > 10000) score += 10
  if (co2 > 50000) score += 10
  if (co2 > 100000) score += 10
  if (assetCount > 10) score += 5
  if (assetCount > 25) score += 5
  return Math.min(Math.round(score), 100)
})

// Fleet Operations Intelligence
const dayOfWeekData = computed(() => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const dayAbbrs = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
  const dayColors = ['#64748B', '#10B981', '#059669', '#0D9488', '#0891B2', '#003A6D', '#94A3B8']
  const dayMap = {}
  const dayCountMap = {}

  days.forEach((d, i) => { dayMap[i] = 0; dayCountMap[i] = 0 })

  transactions.value.forEach(txn => {
    const dayIndex = new Date(txn.transaction_datetime ?? 0).getDay()
    dayMap[dayIndex] += (txn.quantity ?? 0)
    dayCountMap[dayIndex]++
  })

  const maxVolume = Math.max(...Object.values(dayMap), 1)

  return days.map((name, i) => ({
    name,
    abbr: dayAbbrs[i],
    volume: dayMap[i],
    count: dayCountMap[i],
    percentage: (dayMap[i] / maxVolume) * 100,
    color: dayColors[i]
  }))
})

const topAssetsByFrequency = computed(() => {
  const frequencyMap = {}
  transactions.value.forEach(txn => {
    const key = txn.vehicle_no ?? ''
    if (!frequencyMap[key]) frequencyMap[key] = 0
    frequencyMap[key]++
  })
  return Object.entries(frequencyMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
})

// Asset Rankings
const assetRankings = computed(() => {
  const assetMap = {}
  transactions.value.forEach(txn => {
    const key = txn.vehicle_no ?? ''
    if (!assetMap[key]) assetMap[key] = { name: key, volume: 0, transactions: 0 }
    assetMap[key].volume += (txn.quantity ?? 0)
    assetMap[key].transactions++
  })

  const totalVolume = transactions.value.reduce((s, t) => s + (t.quantity ?? 0), 0)

  return Object.values(assetMap)
    .map(a => ({
      ...a,
      avgPerTxn: a.transactions > 0 ? a.volume / a.transactions : 0,
      co2Saved: a.volume * co2FactorFor(),
      sharePercent: totalVolume > 0 ? (a.volume / totalVolume) * 100 : 0,
    }))
    .sort((a, b) => b.volume - a.volume)
})

const TZ = 'Asia/Dubai'

// Helpers
const formatDateInput = (date) => formatInTimeZone(date, TZ, 'yyyy-MM-dd')

const formatChange = (value) => {
  if (value === null || value === undefined) return 'N/A'
  return Math.abs(value).toFixed(1) + '%'
}

const formatNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  return Math.round(num).toLocaleString()
}

const formatCompact = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toFixed(0)
}

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${date.getFullYear()}`
}

// Date initialization
const initializeDates = () => {
  let start, end, compStart, compEnd

  switch (selectedDateRange.value.value) {
    case 'lastweek':
      end = new Date(); start = new Date(); start.setDate(end.getDate() - 7)
      compEnd = new Date(start); compStart = new Date(compEnd); compStart.setDate(compEnd.getDate() - 7)
      break
    case 'lastmonth':
      end = new Date(); start = new Date(); start.setMonth(end.getMonth() - 1)
      compEnd = new Date(start); compStart = new Date(compEnd); compStart.setMonth(compEnd.getMonth() - 1)
      break
    case 'last3months':
      end = new Date(); start = new Date(); start.setMonth(end.getMonth() - 3)
      compEnd = new Date(start); compStart = new Date(compEnd); compStart.setMonth(compEnd.getMonth() - 3)
      break
    case 'last6months':
      end = new Date(); start = new Date(); start.setMonth(end.getMonth() - 6)
      compEnd = new Date(start); compStart = new Date(compEnd); compStart.setMonth(compEnd.getMonth() - 6)
      break
    case 'lastyear':
      end = new Date(); start = new Date(); start.setFullYear(end.getFullYear() - 1)
      compEnd = new Date(start); compStart = new Date(compEnd); compStart.setFullYear(compEnd.getFullYear() - 1)
      break
    case 'ytd': {
      end = new Date(); start = new Date(end.getFullYear(), 0, 1)
      const lastYear = end.getFullYear() - 1
      compEnd = new Date(start); compEnd.setDate(compEnd.getDate() - 1)
      compStart = new Date(lastYear, 0, 1)
      break
    }
    case 'custom':
      return
  }

  startDate.value = formatDateInput(start)
  endDate.value = formatDateInput(end)
  comparisonStartDate.value = formatDateInput(compStart)
  comparisonEndDate.value = formatDateInput(compEnd)
}

initializeDates()

// Data fetching. Generation counter: every trigger takes a new generation and
// only the newest in-flight fetch may write state, so rapid customer/filter
// switches can't let a stale (slower) response overwrite the latest one.
let fetchGen = 0

const fetchTransactions = async () => {
  if (!customersMcs.value.length) {
    // Nothing to fetch (e.g. portal admin with no customer selected). Never
    // leave the full-page spinner up — the picker / empty-state must render.
    fetchGen++
    isLoading.value = false
    return
  }
  if (selectedCustomerIndex.value >= customersMcs.value.length) {
    selectedCustomerIndex.value = 0
  }

  const myGen = ++fetchGen
  isLoading.value = true
  const apiUrl = store.state.apiUrl
  const customer_id = customersMcs.value[selectedCustomerIndex.value].mcs_customer_id

  try {
    const response = await authFetch(
      `${apiUrl}api/v1/customers/${customer_id}/transactions?start_date=${startDate.value}&end_date=${endDate.value}`,
      {},
      store.state.user
    )
    if (myGen !== fetchGen) return
    if (response.ok) {
      const result = await response.json()
      if (myGen !== fetchGen) return
      let fetched = result.data

      const entitiesSet = new Set()
      fetched.forEach(t => entitiesSet.add(t.vehicle_no))
      allEntities.value = Array.from(entitiesSet).map(name => ({ name }))

      if (selectedEntities.value.length) {
        fetched = fetched.filter(t =>
          selectedEntities.value.map(e => e.name).includes(t.vehicle_no)
        )
      }
      transactions.value = fetched
    }

    comparisonLabel.value = `${formatDate(new Date(comparisonStartDate.value))} - ${formatDate(new Date(comparisonEndDate.value))}`

    const prevResponse = await authFetch(
      `${apiUrl}api/v1/customers/${customer_id}/transactions?start_date=${comparisonStartDate.value}&end_date=${comparisonEndDate.value}`,
      {},
      store.state.user
    )
    if (myGen !== fetchGen) return
    if (prevResponse.ok) {
      const result = await prevResponse.json()
      if (myGen !== fetchGen) return
      let fetchedPrev = result.data
      if (selectedEntities.value.length) {
        fetchedPrev = fetchedPrev.filter(t =>
          selectedEntities.value.map(e => e.name).includes(t.vehicle_no)
        )
      }
      previousTransactions.value = fetchedPrev
    }

    updateKPIs()
    updateCharts()
  } catch (err) {
    if (myGen !== fetchGen) return
    console.error('Error fetching transactions:', err)
    showError('Failed to load analytics data')
  } finally {
    if (myGen === fetchGen) isLoading.value = false
  }
}

// KPI computation
const updateKPIs = () => {
  const totalVolume = transactions.value.reduce((s, t) => s + (t.quantity ?? 0), 0)
  const prevVolume = previousTransactions.value.reduce((s, t) => s + (t.quantity ?? 0), 0)
  const volumeChange = prevVolume > 0 ? ((totalVolume - prevVolume) / prevVolume) * 100 : null

  const numTxn = transactions.value.length
  const prevTxn = previousTransactions.value.length
  const txnChange = prevTxn > 0 ? ((numTxn - prevTxn) / prevTxn) * 100 : null

  const co2 = totalVolume * co2FactorFor()
  const prevCo2 = prevVolume * co2FactorFor()
  const co2Change = prevCo2 > 0 ? ((co2 - prevCo2) / prevCo2) * 100 : null

  const avgVol = numTxn > 0 ? totalVolume / numTxn : 0
  const prevAvg = prevTxn > 0 ? prevVolume / prevTxn : 0
  const avgChange = prevAvg > 0 ? ((avgVol - prevAvg) / prevAvg) * 100 : null

  const activeAssets = new Set(transactions.value.map(t => t.vehicle_no ?? '')).size
  const prevActiveAssets = new Set(previousTransactions.value.map(t => t.vehicle_no ?? '')).size
  const assetsChange = prevActiveAssets > 0 ? ((activeAssets - prevActiveAssets) / prevActiveAssets) * 100 : null

  const fuelingFreq = activeAssets > 0 ? (numTxn / activeAssets) : 0
  const prevFreq = prevActiveAssets > 0 ? (prevTxn / prevActiveAssets) : 0
  const freqChange = prevFreq > 0 ? ((fuelingFreq - prevFreq) / prevFreq) * 100 : null

  fleetMetrics.value = [
    { label: 'Total Volume', value: formatCompact(totalVolume) + ' L', change: volumeChange, color: '#10B981' },
    { label: 'Transactions', value: formatNumber(numTxn), change: txnChange, color: '#059669' },
    { label: 'CO2 Saved', value: formatCompact(co2) + ' Kg', change: co2Change, color: '#047857' },
    { label: 'Avg / Transaction', value: formatNumber(Math.round(avgVol)) + ' L', change: avgChange, color: '#0D9488' },
    { label: 'Active Fleet', value: activeAssets.toString(), change: assetsChange, color: '#003A6D' },
    { label: 'Fuel Frequency', value: fuelingFreq.toFixed(1) + 'x', change: freqChange, color: '#0891B2' },
  ]
}

// Chart updates
const updateCharts = () => {
  // Volume over time
  const volumeByDate = {}
  transactions.value.forEach(txn => {
    const dateKey = (txn.transaction_datetime ?? '').split('T')[0]
    volumeByDate[dateKey] = (volumeByDate[dateKey] || 0) + (txn.quantity ?? 0)
  })

  const sortedDates = Object.keys(volumeByDate).sort()
  volumeOverTimeData.value = {
    labels: sortedDates,
    datasets: [{
      label: 'Volume (L)',
      data: sortedDates.map(d => volumeByDate[d]),
      borderColor: chartColors.accent,
      backgroundColor: chartColors.accent + '20',
      fill: true,
    }]
  }

  // CO2 over time
  const co2ByDate = {}
  transactions.value.forEach(txn => {
    const dateKey = (txn.transaction_datetime ?? '').split('T')[0]
    co2ByDate[dateKey] = (co2ByDate[dateKey] || 0) + ((txn.quantity ?? 0) * co2FactorFor())
  })

  co2SavingsOverTimeData.value = {
    labels: sortedDates,
    datasets: [{
      label: 'CO2 Saved (Kg)',
      data: sortedDates.map(d => co2ByDate[d] || 0),
      borderColor: '#047857',
      backgroundColor: '#04785720',
      fill: true,
    }]
  }

  // Cumulative CO2
  let running = 0
  const cumulativeValues = sortedDates.map(d => {
    running += (co2ByDate[d] || 0)
    return running
  })

  cumulativeCo2Data.value = {
    labels: sortedDates,
    datasets: [{
      label: 'Cumulative CO2 Saved (Kg)',
      data: cumulativeValues,
      borderColor: '#059669',
      backgroundColor: 'rgba(5,150,105,0.08)',
      fill: true,
    }]
  }

  // Top entities (horizontal bar)
  const entityVolumeMap = {}
  transactions.value.forEach(txn => {
    const key = txn.vehicle_no ?? ''
    entityVolumeMap[key] = (entityVolumeMap[key] || 0) + (txn.quantity ?? 0)
  })

  const sorted = Object.entries(entityVolumeMap).sort((a, b) => b[1] - a[1]).slice(0, 10)
  topEntitiesData.value = {
    labels: sorted.map(e => e[0]),
    datasets: [{
      label: 'Total Volume (L)',
      data: sorted.map(e => e[1]),
      backgroundColor: sorted.map((_, i) => chartColors.palette[i % chartColors.palette.length] + 'CC'),
      borderColor: sorted.map((_, i) => chartColors.palette[i % chartColors.palette.length]),
      borderWidth: 1,
      borderRadius: 4,
    }]
  }

  // Asset distribution (top 5 + others)
  const topFive = sorted.slice(0, 5)
  const othersVolume = sorted.slice(5).reduce((s, e) => s + e[1], 0)
  const distLabels = topFive.map(e => e[0])
  const distValues = topFive.map(e => e[1])
  if (othersVolume > 0) {
    distLabels.push('Others')
    distValues.push(othersVolume)
  }

  assetDistributionData.value = {
    labels: distLabels,
    datasets: [{
      data: distValues,
      backgroundColor: chartColors.palette.slice(0, distLabels.length),
      borderWidth: 0,
    }]
  }

  // Fuel type distribution
  const fuelTypeMap = {}
  transactions.value.forEach(txn => {
    const ft = txn.product_name ?? 'Unknown'
    fuelTypeMap[ft] = (fuelTypeMap[ft] || 0) + (txn.quantity ?? 0)
  })

  fuelTypeDistributionData.value = {
    labels: Object.keys(fuelTypeMap),
    datasets: [{
      data: Object.values(fuelTypeMap),
      backgroundColor: chartColors.palette.slice(0, Object.keys(fuelTypeMap).length),
      borderWidth: 0,
    }]
  }

  // Monthly performance
  const monthlyMap = {}
  const monthlyCo2Map = {}
  transactions.value.forEach(txn => {
    const d = new Date(txn.transaction_datetime ?? 0)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    monthlyMap[key] = (monthlyMap[key] || 0) + (txn.quantity ?? 0)
    monthlyCo2Map[key] = (monthlyCo2Map[key] || 0) + ((txn.quantity ?? 0) * co2FactorFor())
  })

  const sortedMonths = Object.keys(monthlyMap).sort()
  const monthLabels = sortedMonths.map(m => {
    const [year, month] = m.split('-')
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
  })

  monthlyPerformanceData.value = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Volume (L)',
        data: sortedMonths.map(m => monthlyMap[m]),
        backgroundColor: chartColors.accent + 'CC',
        borderColor: chartColors.accent,
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        label: 'CO2 Saved (Kg)',
        data: sortedMonths.map(m => monthlyCo2Map[m]),
        backgroundColor: chartColors.primary + '80',
        borderColor: chartColors.primary,
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: 'y',
      }
    ]
  }

  chartKey.value++
}

// Export
const downloadChart = (chartType) => {
  const canvases = document.querySelectorAll('canvas')
  const chartMap = {
    'volume-trend': 0,
    'co2-trend': 1,
    'top-entities': 2,
    'monthly-performance': 3,
    'fuel-distribution': 4,
    'cumulative-co2': 5,
  }
  const idx = chartMap[chartType]
  if (canvases[idx]) {
    const link = document.createElement('a')
    link.download = `${chartType}-${new Date().toISOString().split('T')[0]}.png`
    link.href = canvases[idx].toDataURL()
    link.click()
  }
}

const exportAllData = () => {
  let csv = 'Fleet Analytics Report\n'
  csv += `Generated: ${new Date().toLocaleDateString()}\n`
  csv += `Date Range: ${startDate.value} to ${endDate.value}\n\n`

  csv += 'Key Metrics\n'
  fleetMetrics.value.forEach(m => { csv += `${m.label},${m.value}\n` })

  csv += '\nEnvironmental Impact\n'
  csv += `CO2 Savings (Kg),${totalCo2Savings.value.toFixed(2)}\n`
  csv += `Equivalent Trees,${equivalentTrees.value}\n`
  csv += `Equivalent Cars Off Road,${equivalentCars.value}\n`
  csv += `Avg CO2 Saved per Trip,${avgCo2PerTransaction.value.toFixed(2)} Kg\n`
  csv += `Sustainability Score,${sustainabilityScore.value}/100\n`

  csv += `\n${entityLabel.value} Rankings\n`
  csv += `${entityLabel.value},Volume (L),Transactions,Avg/Txn,CO2 Saved (Kg),Share %\n`
  assetRankings.value.forEach(a => {
    csv += `${a.name},${a.volume.toFixed(2)},${a.transactions},${a.avgPerTxn.toFixed(2)},${a.co2Saved.toFixed(2)},${a.sharePercent.toFixed(1)}\n`
  })

  csv += '\nTransaction Data\n'
  csv += `Date,${entityLabel.value},Volume (L),Fuel Type,CO2 Savings (Kg)\n`
  transactions.value.forEach(txn => {
    const qty = txn.quantity ?? 0
    csv += `${txn.transaction_datetime ?? ''},${txn.vehicle_no ?? ''},${qty},${txn.product_name ?? 'Unknown'},${(qty * co2FactorFor()).toFixed(2)}\n`
  })

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.download = `fleet-analytics-${new Date().toISOString().split('T')[0]}.csv`
  link.href = URL.createObjectURL(blob)
  link.click()
  success('Analytics data exported successfully')
}

const refreshData = async () => {
  isRefreshing.value = true
  await fetchTransactions()
  isRefreshing.value = false
}

// Lifecycle

// When the effective customer set changes (portal admin switching customers),
// per-customer filter state must not leak into the new customer's view.
// Created BEFORE the fetch-triggering watcher so the reset flushes first.
const customerIdsKey = computed(() =>
  customersMcs.value.map((c) => c.mcs_customer_id).join(',')
)
watch(customerIdsKey, () => {
  if (selectedEntities.value.length) selectedEntities.value = []
  if (selectedCustomerIndex.value !== 0) selectedCustomerIndex.value = 0
})

watch(customersMcs, (list) => {
  if (list.length) {
    fetchTransactions()
  } else if (userProfile.value) {
    // Profile loaded but nothing to fetch (portal admin with no selection, or
    // account not yet configured): drop the spinner so the picker and the
    // 'No customer selected' empty-state can render.
    fetchGen++
    isLoading.value = false
  }
}, { immediate: true })

watch([startDate, endDate, comparisonStartDate, comparisonEndDate, selectedEntities], fetchTransactions)
watch(selectedCustomerIndex, fetchTransactions)

watch(selectedDateRange, () => {
  initializeDates()
  if (selectedDateRange.value.value !== 'custom') fetchTransactions()
})

watch([startDate, endDate], () => {
  if (selectedDateRange.value.value === 'custom') {
    const s = new Date(startDate.value)
    const e = new Date(endDate.value)
    const diff = (e - s) / (1000 * 60 * 60 * 24)
    const ce = new Date(s); ce.setDate(s.getDate() - 1)
    const cs = new Date(ce); cs.setDate(ce.getDate() - diff)
    comparisonStartDate.value = formatDateInput(cs)
    comparisonEndDate.value = formatDateInput(ce)
  }
}, { immediate: true })
</script>

<style>
@import "../../../node_modules/vue-multiselect/dist/vue-multiselect.css";

.multiselect-tailwind {
  @apply rounded-xl;
}
.multiselect-tailwind .multiselect__tags {
  @apply rounded-xl border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800;
}
.multiselect-tailwind .multiselect__input,
.multiselect-tailwind .multiselect__single {
  @apply text-surface-900 dark:text-surface-100 bg-transparent;
}
.multiselect-tailwind .multiselect__tag {
  @apply bg-accent-500 text-white rounded-lg;
}
.multiselect-tailwind .multiselect__content-wrapper {
  @apply border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-800 rounded-xl mt-1;
}
.multiselect-tailwind .multiselect__option {
  @apply text-surface-900 dark:text-surface-100;
}
.multiselect-tailwind .multiselect__option--highlight {
  @apply bg-accent-500 text-white;
}
</style>

<style scoped>
/* KPI Cards */
.kpi-card {
  @apply relative overflow-hidden bg-white dark:bg-surface-900/80 rounded-2xl p-4 border border-surface-200/60 dark:border-surface-700/60;
  @apply hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300;
  animation: fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

/* Analytics Card Base */
.analytics-card {
  @apply rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80;
}

/* Impact Metric Cards */
.impact-metric-card {
  @apply relative bg-white dark:bg-surface-900/60 rounded-2xl p-5 border;
  @apply hover:shadow-md transition-all duration-300;
}

.impact-icon {
  @apply inline-flex items-center justify-center w-9 h-9 rounded-xl;
}

/* Rankings Table */
.ranking-th {
  @apply text-[10px] font-bold uppercase tracking-[0.1em] text-surface-400 dark:text-surface-500 py-3 px-3;
}

.ranking-row {
  @apply border-b border-surface-100 dark:border-surface-800/60 hover:bg-surface-50 dark:hover:bg-surface-800/30 transition-colors;
}

.ranking-td {
  @apply py-3 px-3 text-xs text-surface-700 dark:text-surface-300;
}

/* Animate sections */
.animate-fade-up {
  animation-fill-mode: backwards;
}
</style>
